import React from 'react';
import { Contact, Phone, Mail } from 'lucide-react';
import type { DealContact, ClientContact } from '../../lib/types';

interface DealContactsSectionProps {
  dealContacts?: DealContact[];
  customerContacts?: ClientContact[];
  clientPhone?: string | null;
  clientEmail?: string | null;
}

export function DealContactsSection({ dealContacts, customerContacts, clientPhone, clientEmail }: DealContactsSectionProps) {
  // Filter out contacts without contact data
  const validDealContacts = dealContacts?.filter(dc => dc.contact) || [];

  // Determine which contacts to show and the title
  const isShowingCustomerContacts = validDealContacts.length === 0 && (customerContacts?.length ?? 0) > 0;
  const contactsToShow = isShowingCustomerContacts ? customerContacts! : validDealContacts.map(dc => dc.contact!);
  const title = isShowingCustomerContacts ? 'Contatti del cliente' : 'Contatti associati all\'offerta';

  // Fallback contact from customerContacts for missing phone/email on deal contacts
  const fallbackContact = !isShowingCustomerContacts && customerContacts?.length
    ? (customerContacts.find(c => c.is_referente) || customerContacts.find(c => c.is_primary) || customerContacts[0])
    : null;

  // Se non ci sono contatti da mostrare, fallback al recapito diretto del cliente
  if (contactsToShow.length === 0) {
    if (clientPhone || clientEmail) {
      return (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Contact className="h-4 w-4 text-[#787774] dark:text-gray-400" />
            <h3 className="text-sm font-medium text-[#37352F] dark:text-white">
              Recapito cliente
            </h3>
          </div>
          <div className="px-3 py-2 rounded-md bg-[rgba(55,53,47,0.03)] dark:bg-white/[0.04]">
            <div className="text-[13px] text-[#787774] dark:text-gray-400 flex items-center gap-3">
              {clientEmail && (
                <a
                  href={`mailto:${clientEmail}`}
                  className="flex items-center gap-1 hover:text-[#37352F] dark:hover:text-white truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  {clientEmail}
                </a>
              )}
              {clientPhone && (
                <a
                  href={`tel:${clientPhone}`}
                  className="flex items-center gap-1 hover:text-[#37352F] dark:hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  {clientPhone}
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Contact className="h-4 w-4 text-[#787774] dark:text-gray-400" />
        <h3 className="text-sm font-medium text-[#37352F] dark:text-white">
          {title}
          <span className="ml-2 text-xs font-normal text-[#787774] dark:text-gray-400">
            ({contactsToShow.length})
          </span>
        </h3>
      </div>

      {/* Compact contact list - Notion style */}
      <div className="space-y-2">
        {contactsToShow.map((contact) => {
          // Fallback: se il deal contact non ha phone/email, prendi dal contatto cliente o dal client diretto
          const displayPhone = contact.phone || fallbackContact?.phone || clientPhone || null;
          const displayEmail = contact.email || fallbackContact?.email || clientEmail || null;

          return (
            <div
              key={contact.id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[rgba(55,53,47,0.03)] dark:bg-white/[0.04] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#37352F] dark:text-white truncate">
                    {contact.name}
                  </span>
                  {contact.role && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(55,53,47,0.06)] dark:bg-gray-700 text-[#787774] dark:text-gray-400 flex-shrink-0">
                      {contact.role}
                    </span>
                  )}
                </div>
                {/* Email e telefono come testo */}
                {(displayEmail || displayPhone) && (
                  <div className="mt-1 text-[13px] text-[#787774] dark:text-gray-400 flex items-center gap-2">
                    {displayEmail && (
                      <a
                        href={`mailto:${displayEmail}`}
                        className="hover:text-[#37352F] dark:hover:text-white truncate max-w-[200px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {displayEmail}
                      </a>
                    )}
                    {displayEmail && displayPhone && <span>•</span>}
                    {displayPhone && (
                      <a
                        href={`tel:${displayPhone}`}
                        className="hover:text-[#37352F] dark:hover:text-white"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {displayPhone}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
